import {
  EMAIL_CONTEXTS,
  EMAIL_CONTEXT_LABELS,
  EMAIL_FIELDS,
  renderEmailTemplate,
  sampleEmailValues,
  unresolvedEmailTokens,
  type CompanySettings,
  type EmailContext,
  type EmailTemplate,
} from "@roof-hawk/core";
import { useCallback, useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { deleteRow, insertRow, updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";

/**
 * Email templates.
 *
 * Eighty-five of these came out of ROOF.link, and every one of them was written
 * for a company whose name is being replaced. The import took the old name,
 * number and website out and left {{company.name}} and friends behind, so the
 * company details at the top of this screen are not decoration — they are what
 * those merge fields resolve to, and until they are filled in every template
 * signs off with a placeholder.
 *
 * The list is links rather than an editor, like Roof Templates: eighty-five
 * bodies of several hundred words each will not fit on one screen, and the
 * thing a person actually does here is open one and read it.
 *
 * Nothing on this screen sends anything. Sending is Gmail, and comes later.
 */
export function EmailsTool() {
  const [openId, setOpenId] = useState<string | null>(null);

  const templates = useAsync(
    async () =>
      unwrap<EmailTemplate[]>(
        await supabase
          .from("email_templates")
          .select("*")
          .order("sort_order")
          .order("name"),
      ),
    [],
  );

  if (openId) {
    const template = (templates.data ?? []).find((row) => row.id === openId);
    if (!template) return null;
    return (
      <TemplateEditor
        template={template}
        onBack={async () => {
          setOpenId(null);
          await templates.reload();
        }}
      />
    );
  }

  return (
    <TemplateList
      templates={templates.data ?? []}
      error={templates.error}
      onOpen={setOpenId}
      onReload={templates.reload}
    />
  );
}

/** The company's own details, which every {{company.*}} field reads from. */
function CompanyDetails() {
  const [error, setError] = useState<string | null>(null);

  const settings = useAsync(
    async () =>
      unwrap<(CompanySettings & { id: number })[]>(
        await supabase.from("company_settings").select("*").limit(1),
      ),
    [],
  );

  const row = settings.data?.[0];
  if (!row) return null;

  const save = async (field: keyof CompanySettings, value: string) => {
    try {
      // updateRow, not a bare supabase call: supabase-js resolves with
      // { error } rather than throwing, so a bare await would swallow an RLS
      // refusal, reload, and quietly put the old value back. This is the one
      // row every {{company.*}} field in all 85 templates reads from — a save
      // that silently does nothing here is a rebrand that silently does not
      // happen.
      await updateRow("company_settings", String(row.id), {
        [field]: value || null,
      });
      setError(null);
      await settings.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Translated where drawn; the labels and the English placeholders are in
  // the dictionary, the address-shaped placeholders read the same anywhere.
  const fields: [keyof CompanySettings, string, string][] = [
    ["name", "Company name", "Not set — templates will show {{company.name}}"],
    ["phone", "Phone", "Office number"],
    ["email", "Email", "office@…"],
    [
      "cancellation_email",
      "Cancellation email",
      "Leave empty to use the email above",
    ],
    ["website", "Website", "www.…"],
    ["facebook", "Facebook", "facebook.com/…"],
  ];

  return (
    <div className="shelf" style={{ marginBottom: 14 }}>
      <b>{t("Your company details")}</b>
      <ErrorNote error={error} />
      <div className="fgrid">
        {fields.map(([field, label, placeholder]) => (
          <div className="field" key={field}>
            <label>{t(label)}</label>
            <TextInput
              value={row[field] ?? ""}
              placeholder={t(placeholder)}
              onSave={(value) => void save(field, value)}
            />
          </div>
        ))}
      </div>
      <div className="na-hint" style={{ marginTop: 8 }}>
        {t(
          "These fill the {{company.…}} fields in every template. The import took the old company name out and left those placeholders behind, so filling this in once rebrands all of them. Cancellation email is the exception: it appears on the contract only, as the address a customer writes to inside their three days to cancel.",
        )}
      </div>
    </div>
  );
}

function TemplateList({
  templates,
  error,
  onOpen,
  onReload,
}: {
  templates: readonly EmailTemplate[];
  error: string | null;
  onOpen: (id: string) => void;
  onReload: () => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [context, setContext] = useState<EmailContext | "all">("all");
  const [onlyReview, setOnlyReview] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const needle = search.trim().toLowerCase();
  const shown = templates.filter((template) => {
    if (context !== "all" && template.context !== context) return false;
    if (onlyReview && !template.needs_review) return false;
    if (!needle) return true;
    return `${template.name} ${template.subject} ${template.body}`
      .toLowerCase()
      .includes(needle);
  });

  const reviewCount = templates.filter(
    (template) => template.needs_review,
  ).length;

  return (
    <>
      <ErrorNote error={error ?? addError} />
      <CompanyDetails />

      <div className="toolbar2">
        <input
          placeholder={t("Search templates…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={context}
          onChange={(e) => setContext(e.target.value as EmailContext | "all")}
        >
          <option value="all">{t("Every kind")}</option>
          {EMAIL_CONTEXTS.map((c) => (
            <option key={c} value={c}>
              {t(EMAIL_CONTEXT_LABELS[c])}
            </option>
          ))}
        </select>
        {reviewCount > 0 && (
          <button
            className={`addrow ghost${onlyReview ? " active" : ""}`}
            style={{ marginTop: 0 }}
            onClick={() => setOnlyReview((v) => !v)}
          >
            {onlyReview
              ? t("Showing {n} needing review", { n: reviewCount })
              : t("Show {n} needing review", { n: reviewCount })}
          </button>
        )}
        <span className="count">
          {tn(shown.length, "1 template", "{n} templates")}
        </span>
        <button
          className="addrow"
          style={{ marginTop: 0 }}
          onClick={() =>
            void (async () => {
              try {
                const created = await insertRow<EmailTemplate>(
                  "email_templates",
                  {
                    name: "New template",
                    context: "job",
                    sort_order: (templates.length + 1) * 10,
                  },
                );
                setAddError(null);
                await onReload();
                onOpen(created.id);
              } catch (e) {
                setAddError(e instanceof Error ? e.message : String(e));
              }
            })()
          }
        >
          {t("+ Add")}
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="shelf">
          <b>{t("No templates yet")}</b>
          {t("Import the ROOF.link export with")}{" "}
          <code>node scripts/import-email-templates.mjs</code>
          {t(", or add one here.")}
        </div>
      ) : (
        <div className="list">
          <div className="lrow head emailrow">
            <div>{t("Template")}</div>
            <div>{t("Subject")}</div>
            <div>{t("Kind")}</div>
            <div>{t("Status")}</div>
          </div>
          {shown.map((template) => (
            <div
              key={template.id}
              className="lrow emailrow"
              onClick={() => onOpen(template.id)}
            >
              <div className="cust">{template.name}</div>
              <div className="sub">{template.subject || "—"}</div>
              <div className="sub">
                {t(EMAIL_CONTEXT_LABELS[template.context])}
              </div>
              <div className="sub">
                {!template.active && (
                  <span className="badge draft">{t("off")}</span>
                )}
                {template.needs_review && (
                  <span className="badge draft">{t("needs review")}</span>
                )}
                {template.active && !template.needs_review && (
                  <span className="badge approved">{t("ready")}</span>
                )}
              </div>
            </div>
          ))}
          {shown.length === 0 && (
            <div className="lrow">
              <div className="na-hint">{t("Nothing matches that.")}</div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * One template.
 *
 * The preview is filled with obvious fakes rather than a real job — a template
 * is written long before it is sent to anybody, and a preview that showed a
 * customer's details would be a preview somebody could mistake for the email.
 */
function TemplateEditor({
  template,
  onBack,
}: {
  template: EmailTemplate;
  onBack: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [row, setRow] = useState(template);
  const [preview, setPreview] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const save = useCallback(
    async (patch: Partial<EmailTemplate>) => {
      // Applied first so typing stays responsive, and put back if the write
      // fails — a template that reads as saved but is not is how somebody
      // sends the old wording a fortnight later. `??=` so the snapshot is the
      // pre-patch row even when React invokes the updater twice.
      let before: EmailTemplate | null = null;
      setRow((current) => {
        before ??= current;
        return { ...current, ...patch };
      });
      try {
        await updateRow("email_templates", template.id, patch);
        setError(null);
      } catch (e) {
        if (before) setRow(before);
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [template.id],
  );

  /** Drop a merge field in where the cursor is, not at the end. */
  const insertField = (token: string) => {
    const area = bodyRef.current;
    const text = `{{${token}}}`;
    if (!area) {
      void save({ body: `${row.body}${text}` });
      return;
    }
    const start = area.selectionStart ?? row.body.length;
    const end = area.selectionEnd ?? start;
    const body = row.body.slice(0, start) + text + row.body.slice(end);
    void save({ body });
    // After React has painted the new value, or the caret lands in the old one.
    setTimeout(() => {
      area.focus();
      area.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  const sample = sampleEmailValues();
  const missing = unresolvedEmailTokens(`${row.subject} ${row.body}`, sample);

  const groups = [...new Set(EMAIL_FIELDS.map((f) => f.group))];

  return (
    <>
      <div className="tmplhead">
        <span className="clink" onClick={() => void onBack()}>
          {t("← All templates")}
        </span>
        <h3>{row.name}</h3>
        <span className="na-hint">
          {t(EMAIL_CONTEXT_LABELS[row.context])}
          {row.source
            ? " · " + t("imported from {source}", { source: row.source })
            : " · " + t("written here")}
        </span>
      </div>

      <ErrorNote error={error} />

      {row.needs_review && (
        <div className="shelf" style={{ marginBottom: 14 }}>
          <b>{t("The import changed something it was not sure about")}</b>
          <ul style={{ margin: "6px 0 10px 18px" }}>
            {row.review_notes.map((note) => (
              <li key={note} className="sub">
                {note}
              </li>
            ))}
          </ul>
          <button
            className="addrow"
            style={{ marginTop: 0 }}
            onClick={() => void save({ needs_review: false, review_notes: [] })}
          >
            {t("Looks right — clear this")}
          </button>
        </div>
      )}

      <div className="fgrid">
        <div className="field">
          <label>{t("Name")}</label>
          <TextInput value={row.name} onSave={(name) => void save({ name })} />
        </div>
        <div className="field">
          <label>{t("Used for")}</label>
          <select
            value={row.context}
            onChange={(e) =>
              void save({ context: e.target.value as EmailContext })
            }
          >
            {EMAIL_CONTEXTS.map((c) => (
              <option key={c} value={c}>
                {t(EMAIL_CONTEXT_LABELS[c])}
              </option>
            ))}
          </select>
        </div>
        <div className="field full">
          <label>{t("Subject")}</label>
          <TextInput
            value={row.subject}
            onSave={(subject) => void save({ subject })}
          />
        </div>
      </div>

      <div className="toolbar2" style={{ marginTop: 10 }}>
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) insertField(e.target.value);
            e.target.value = "";
          }}
        >
          <option value="">{t("Insert a merge field…")}</option>
          {groups.map((group) => (
            <optgroup key={group} label={t(group)}>
              {EMAIL_FIELDS.filter((f) => f.group === group).map((f) => (
                <option key={f.token} value={f.token}>
                  {t(f.label)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <button
          className="addrow ghost"
          style={{ marginTop: 0 }}
          onClick={() => setPreview((v) => !v)}
        >
          {preview ? t("Edit") : t("Preview")}
        </button>
        <label className="sub" style={{ display: "flex", gap: 6 }}>
          <input
            type="checkbox"
            checked={row.active}
            onChange={(e) => void save({ active: e.target.checked })}
          />
          {t("In use")}
        </label>
        <span className="count">
          {missing.length === 0
            ? t("every field can be filled")
            : tn(
                missing.length,
                "1 field Roof Board cannot fill",
                "{n} fields Roof Board cannot fill",
              )}
        </span>
      </div>

      {preview ? (
        <div className="shelf" style={{ whiteSpace: "pre-wrap" }}>
          <b>{renderEmailTemplate(row.subject, sample) || t("(no subject)")}</b>
          {"\n\n"}
          {renderEmailTemplate(row.body, sample)}
        </div>
      ) : (
        <div className="field full">
          <label htmlFor="email-template-body">{t("Body")}</label>
          <textarea
            id="email-template-body"
            ref={bodyRef}
            style={{ minHeight: 380 }}
            value={row.body}
            onChange={(e) => setRow({ ...row, body: e.target.value })}
            onBlur={() => void save({ body: row.body })}
          />
        </div>
      )}

      {missing.length > 0 && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Left as written because Roof Board has nothing to put there: {fields}. They will show up like that in the sent email unless you take them out.",
            { fields: missing.map((token) => `{{${token}}}`).join(", ") },
          )}
        </div>
      )}

      <button
        className="addrow ghost"
        style={{ marginTop: 18 }}
        onClick={() => {
          if (
            !window.confirm(
              t('Delete "{name}"? This cannot be undone.', { name: row.name }),
            )
          )
            return;
          void (async () => {
            try {
              await deleteRow("email_templates", template.id);
              await onBack();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            }
          })();
        }}
      >
        {t("Delete this template")}
      </button>
    </>
  );
}

/** An input that saves when you leave it, like the rest of Tools. */
function TextInput({
  value,
  placeholder,
  onSave,
}: {
  value: string;
  placeholder?: string;
  onSave: (value: string) => void;
}) {
  const [local, setLocal] = useState(value);
  const [dirty, setDirty] = useState(false);
  if (!dirty && local !== value) setLocal(value);

  return (
    <input
      value={local}
      placeholder={placeholder}
      onChange={(e) => {
        setLocal(e.target.value);
        setDirty(true);
      }}
      onBlur={() => {
        if (!dirty) return;
        setDirty(false);
        onSave(local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
